// Setup wizard (owner request 14 Oct 2026) — /setup — and the start checklist on Today.
// Seven steps, each skippable and resumable: profile & ICCS registration with the licence classes (life / general),
// office & availability, importing clients, calendar & e-mail, the avrastro.com booking link, which tools to show,
// backups & security. Progress lives in settings (domain/setup.ts); a step is done when the data shows it or the
// advisor marks it. The Today card stays until every step is done or skipped (or is hidden for a week).
import { useEffect, useMemo, useState } from 'react';
import {
  SETUP_STEPS, PRACTICE_CAPACITIES, MEETING_MODES, ALWAYS_SHOWN_MODULES, brandingProfiles, getOffice, getPractice, getSetupState, hiddenModules, bookingUrl,
  markSetupStep, regionPack, resumeSetup, saveBookingUrl, saveBranding, saveHiddenModules, saveOffice, savePractice, saveSetting, setupProgress, showSetupCard, snoozeSetup,
  getSetting, sectionShown, type Changeset, type SetupProgress, type SetupSignals, type SetupStep,
} from '@vero/domain';
import { useRuntime } from '../core/runtime.tsx';
import { Field, PageHead } from '../core/ui.tsx';
import { Icon } from '../core/icons.tsx';
import { navigate, useRoute } from '../core/router.ts';
import { MODULES, SPACES } from '../core/registry.tsx';
import { openExternal } from '../core/links.ts';
import { backupAgeDays, useSaveStatus } from '../core/save-status.ts';
import { accountService } from '../core/account-service.ts';
import { useAccount } from './account.tsx';
import { openers } from './forms.tsx';
import { ClientImport } from './client-import.tsx';
import { BackupCard } from './data.tsx';
import { CatalogManager } from './catalog.tsx';

const STEP_ICON: Record<SetupStep, string> = { profile: 'shield', office: 'pin', clients: 'people', connect: 'agenda', bookings: 'globe', modules: 'settings', safety: 'lock' };

/** facts only the app can see: last backup file, encryption, Outlook link */
export function useSetupSignals(): SetupSignals {
  const rt = useRuntime();
  const save = useSaveStatus();
  const a = useAccount();
  const [outlook, setOutlook] = useState(false);
  useEffect(() => {
    if (!a?.signedIn) { setOutlook(false); return; }
    let on = true;
    void accountService()?.api.outlookStatus().then((s) => { if (on) setOutlook(!!s?.linked); }).catch(() => undefined);
    return () => { on = false; };
  }, [a?.signedIn]);
  return { backupAgeDays: backupAgeDays(save), encrypted: rt.engine.includes('aes-gcm'), outlookConnected: outlook };
}
export function useSetupProgress(): SetupProgress {
  const rt = useRuntime();
  const signals = useSetupSignals();
  return useMemo(() => setupProgress(rt.view, rt.today, signals), [rt.view, rt.today, signals.backupAgeDays, signals.encrypted, signals.outlookConnected]);
}

/** Today: the start checklist with progress, until every step is done or skipped */
export function SetupCard() {
  const rt = useRuntime();
  const { t } = rt;
  const p = useSetupProgress();
  if (!showSetupCard(p) || rt.ws.readOnlyReason) return null;
  return (
    <section className="card setup-card" aria-labelledby="setup-card-title" data-testid="setup-card">
      <div className="setup-card-head">
        <div className="setup-ring" style={{ ['--p' as string]: p.percent }} role="img" aria-label={t('setup.progress_aria', { percent: p.percent })}><b className="num">{p.percent}%</b></div>
        <div className="grow">
          <h2 id="setup-card-title">{t('setup.card_title')}</h2>
          <p className="small muted" style={{ margin: 0 }}>{t('setup.card_sub', { done: p.done, total: p.total, skipped: p.skipped })}</p>
        </div>
        <div className="row wrap" style={{ gap: 6 }}>
          <button className="btn btn-primary" onClick={() => navigate(`/setup${p.next ? `?step=${p.next}` : ''}`)} data-testid="setup-continue">{t(p.done || p.skipped ? 'setup.continue' : 'setup.start')}</button>
          <button className="btn btn-ghost btn-sm" onClick={() => void rt.run((v, c) => snoozeSetup(v, c, 7), 'setup.snoozed')} data-testid="setup-snooze">{t('setup.snooze')}</button>
        </div>
      </div>
      <ol className="setup-checklist">
        {p.items.map((it) => (
          <li key={it.id} className={it.status}>
            <button className="setup-check" onClick={() => navigate(`/setup?step=${it.id}`)} data-testid={`setup-item-${it.id}`}>
              <span className="setup-dot" aria-hidden="true">{it.status === 'done' ? <Icon name="check" size={13} /> : it.status === 'skipped' ? '–' : ''}</span>
              <span>{t(`setup.step.${it.id}.title`)}</span>
              <span className="sr-only">{t(`setup.status.${it.status}`)}</span>
            </button>
          </li>
        ))}
      </ol>
    </section>
  );
}

/** /setup — the wizard */
export function SetupView() {
  const rt = useRuntime();
  const { t } = rt;
  const route = useRoute();
  const p = useSetupProgress();
  const asked = route.query.get('step') as SetupStep | 'finish' | null;
  const [step, setStep] = useState<SetupStep | 'finish'>(() => (asked && (asked === 'finish' || (SETUP_STEPS as readonly string[]).includes(asked)) ? asked : p.next ?? 'finish'));
  useEffect(() => { if (asked && asked !== step && (asked === 'finish' || (SETUP_STEPS as readonly string[]).includes(asked))) setStep(asked); }, [asked]);
  useEffect(() => { if (getSetupState(rt.view).snoozedUntil && !rt.ws.readOnlyReason) void rt.ws.run((v, c) => resumeSetup(v, c)).catch(() => undefined); }, []);
  const idx = step === 'finish' ? SETUP_STEPS.length : SETUP_STEPS.indexOf(step);
  const go = (s: SetupStep | 'finish') => { setStep(s); navigate(`/setup?step=${s}`, { replace: true }); };
  const forward = () => go(SETUP_STEPS[idx + 1] ?? 'finish');
  const mark = async (s: SetupStep, m: 'done' | 'skipped' | null) => { await rt.run((v, c) => markSetupStep(v, s, m, c)); };
  const skip = async () => { if (step !== 'finish') { const it = p.items.find((i) => i.id === step); if (it?.status !== 'done') await mark(step, 'skipped'); } forward(); };
  const status = (s: SetupStep) => p.items.find((i) => i.id === s)!.status;
  return (
    <div data-testid="setup">
      <PageHead title={t('setup.title')} subtitle={t('setup.subtitle')} actions={<button className="btn btn-ghost" onClick={() => navigate('/help?a=setup-wizard')}><Icon name="spark" />{t('setup.help')}</button>} />
      <div className="setup-layout">
        <nav className="card setup-steps" aria-label={t('setup.steps_label')}>
          <div className="setup-progress">
            <div className="row spread small"><b>{t('setup.progress', { percent: p.percent })}</b><span className="muted">{p.done}/{p.total}</span></div>
            <div className="meter lvl-3" role="progressbar" aria-valuenow={p.percent} aria-valuemin={0} aria-valuemax={100} aria-label={t('setup.progress', { percent: p.percent })}><i style={{ width: `${p.percent}%` }} /></div>
          </div>
          <ol>
            {SETUP_STEPS.map((s, i) => (
              <li key={s}><button className={`setup-step ${status(s)}`} aria-current={step === s ? 'step' : undefined} onClick={() => go(s)} data-testid={`setup-step-${s}`}>
                <span className="setup-dot" aria-hidden="true">{status(s) === 'done' ? <Icon name="check" size={13} /> : status(s) === 'skipped' ? '–' : i + 1}</span>
                <span className="grow">{t(`setup.step.${s}.title`)}<span className="sr-only"> · {t(`setup.status.${status(s)}`)}</span></span>
              </button></li>
            ))}
            <li><button className={`setup-step ${p.complete ? 'done' : ''}`} aria-current={step === 'finish' ? 'step' : undefined} onClick={() => go('finish')} data-testid="setup-step-finish">
              <span className="setup-dot" aria-hidden="true"><Icon name="star" size={13} /></span><span className="grow">{t('setup.finish.title')}</span>
            </button></li>
          </ol>
        </nav>
        <section className="card card-pad setup-panel" aria-labelledby="setup-step-title">
          {step !== 'finish' && <div className="setup-panel-head">
            <span className="setup-panel-icon" aria-hidden="true"><Icon name={STEP_ICON[step]} /></span>
            <div className="grow"><div className="tiny muted">{t('setup.step_of', { n: idx + 1, total: SETUP_STEPS.length })}</div><h2 id="setup-step-title">{t(`setup.step.${step}.title`)}</h2><p className="muted small" style={{ margin: 0 }}>{t(`setup.step.${step}.sub`)}</p></div>
            {status(step) !== 'todo' && <span className={`chip ${status(step) === 'done' ? 'ok' : ''}`}>{t(`setup.status.${status(step)}`)}</span>}
          </div>}
          {step === 'profile' && <ProfileStep onSaved={forward} />}
          {step === 'office' && <OfficeStep onSaved={forward} />}
          {step === 'clients' && <ClientsStep />}
          {step === 'connect' && <ConnectStep />}
          {step === 'bookings' && <BookingsStep onSaved={forward} />}
          {step === 'modules' && <ModulesStep onSaved={forward} />}
          {step === 'safety' && <SafetyStep />}
          {step === 'finish' && <FinishStep p={p} go={go} />}
          {step !== 'finish' && <div className="row wrap setup-nav">
            <button className="btn btn-ghost" disabled={idx === 0} onClick={() => go(SETUP_STEPS[idx - 1]!)}>{t('setup.back')}</button>
            <span className="spacer" />
            {status(step) === 'skipped' && <button className="btn btn-ghost" onClick={() => void mark(step, null)} data-testid="setup-resume">{t('setup.unskip')}</button>}
            {status(step) === 'todo' && <button className="btn btn-ghost" onClick={() => void skip()} data-testid="setup-skip">{t('setup.skip')}</button>}
            {(['clients', 'connect', 'safety'] as SetupStep[]).includes(step) && status(step) !== 'done' && <button className="btn" onClick={() => void mark(step, 'done').then(forward)} data-testid="setup-mark-done">{t('setup.mark_done')}</button>}
            <button className="btn btn-primary" form={`setup-form-${step}`} type={FORM_STEPS.includes(step) ? 'submit' : 'button'} onClick={FORM_STEPS.includes(step) ? undefined : forward} data-testid="setup-next">{t(FORM_STEPS.includes(step) ? 'setup.save_next' : 'setup.next')}</button>
          </div>}
        </section>
      </div>
    </div>
  );
}
const FORM_STEPS: SetupStep[] = ['profile', 'office', 'bookings', 'modules'];

/** run several commands as one change (one undo) */
const merge = (action: string, ...parts: Changeset[]): Changeset => ({ action, puts: parts.flatMap((p) => p.puts), deletes: [], summary: action });

function ProfileStep({ onSaved }: { onSaved: () => void }) {
  const rt = useRuntime();
  const { t } = rt;
  const pr = getPractice(rt.view);
  const mine = brandingProfiles(rt.view, rt.userId).advisor;
  const pack = regionPack(rt.region);
  const [f, setF] = useState(() => ({
    name: rt.advisor.name ?? '', company: rt.advisor.company ?? '', regNo: pr.regNo ?? mine?.licenceNumber ?? rt.advisor.license ?? '',
    capacity: pr.capacity ?? '', life: pr.lines.life, general: pr.lines.general, insurers: pr.insurers.join(', '), phone: rt.advisor.phone ?? '', email: rt.advisor.email ?? '',
  }));
  const [err, setErr] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: { target: HTMLInputElement | HTMLSelectElement }) => setF({ ...f, [k]: e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value });
  const submit = async (e: { preventDefault(): void }) => {
    e.preventDefault();
    if (!f.life && !f.general) { setErr(t('errors.setup.lines_required')); return; }
    setErr(null);
    const cs = await rt.run((v, c) => {
      const prev = getSetting<Record<string, string>>(v, 'advisor', {});
      const adv = { ...prev, name: f.name.trim(), company: f.company.trim(), license: f.regNo.trim(), phone: f.phone.trim(), email: f.email.trim() };
      const b = brandingProfiles(v, rt.userId).advisor;
      return merge('setup.profile',
        savePractice(v, { lines: { life: f.life, general: f.general }, capacity: f.capacity || null, regNo: f.regNo, insurers: f.insurers }, c),
        saveSetting(v, 'advisor', adv, c),
        saveBranding(v, { id: b?.id, scope: 'advisor', userId: b?.userId ?? rt.userId, advisorName: adv.name || null, agencyName: adv.company || null, licenceNumber: adv.license || null, licenceAuthority: b?.licenceAuthority || pack.licensing.authority, phone: adv.phone || null, email: adv.email || null, country: rt.region }, c),
      );
    }, 'setup.saved');
    if (cs) onSaved();
  };
  return (
    <>
    <form id="setup-form-profile" className="stack" onSubmit={(e: { preventDefault(): void }) => void submit(e)} data-testid="setup-profile">
      <div className="form-grid">
        <Field label={t('settings.advisor_name')}><input className="input" value={f.name} onChange={set('name')} autoComplete="name" required data-testid="setup-name" /></Field>
        <Field label={t('settings.company')}><input className="input" value={f.company} onChange={set('company')} autoComplete="organization" /></Field>
        <Field label={t('setup.reg_no')} hint={t('setup.reg_no_hint')}><input className="input" value={f.regNo} onChange={set('regNo')} required data-testid="setup-regno" /></Field>
        <Field label={t('setup.capacity')} hint={t('setup.capacity_hint')}>
          <select className="select" value={f.capacity} onChange={set('capacity')} data-testid="setup-capacity"><option value="">{t('setup.choose')}</option>{PRACTICE_CAPACITIES.map((c) => <option key={c} value={c}>{t(`setup.capacity_opt.${c}`)}</option>)}</select>
        </Field>
        <fieldset className="field span2 setup-lines" data-testid="setup-lines">
          <legend>{t('setup.lines')}</legend>
          <label className={`setup-line ${f.life ? 'on' : ''}`}><input type="checkbox" checked={f.life} onChange={set('life')} data-testid="setup-line-life" /><span><b>{t('setup.line.life')}</b><small className="muted">{t('setup.line.life_sub')}</small></span></label>
          <label className={`setup-line ${f.general ? 'on' : ''}`}><input type="checkbox" checked={f.general} onChange={set('general')} data-testid="setup-line-general" /><span><b>{t('setup.line.general')}</b><small className="muted">{t('setup.line.general_sub')}</small></span></label>
        </fieldset>
        <Field label={t('setup.insurers')} span2 hint={t('setup.insurers_hint')}><input className="input" value={f.insurers} onChange={set('insurers')} /></Field>
        <Field label={t('settings.phone')}><input className="input" type="tel" value={f.phone} onChange={set('phone')} autoComplete="tel" /></Field>
        <Field label={t('settings.email')}><input className="input" type="email" value={f.email} onChange={set('email')} autoComplete="email" /></Field>
      </div>
      {err && <p className="callout crit small" role="alert">{err}</p>}
      <p className="tiny muted" style={{ margin: 0 }}>{t('setup.profile_note')}</p>
    </form>
    {/* v14: the same catalogue manager as in Settings — insurers, their categories and plans (import from the list above or from policies) */}
    <div className="setup-catalog" style={{ marginTop: 18 }}><CatalogManager embedded /></div>
    </>
  );
}

function OfficeStep({ onSaved }: { onSaved: () => void }) {
  const rt = useRuntime();
  const { t } = rt;
  const o = getOffice(rt.view);
  const [f, setF] = useState(() => ({ ...o, address: o.address ?? rt.advisor.officeAddress ?? '', mapsUrl: o.mapsUrl ?? rt.advisor.officeMapsUrl ?? '', phone: o.phone ?? '' }));
  const dayName = (d: number) => new Intl.DateTimeFormat(rt.locale, { weekday: 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(2026, 0, 4 + d)));
  const toggle = <T,>(list: T[], x: T) => (list.includes(x) ? list.filter((y) => y !== x) : [...list, x]);
  const submit = async (e: { preventDefault(): void }) => {
    e.preventDefault();
    const cs = await rt.run((v, c) => {
      const prev = getSetting<Record<string, string>>(v, 'advisor', {});
      return merge('setup.office',
        saveOffice(v, { days: f.days, from: f.from, to: f.to, meetingMinutes: Number(f.meetingMinutes), modes: f.modes, address: f.address, mapsUrl: f.mapsUrl, phone: f.phone }, c),
        saveSetting(v, 'advisor', { ...prev, officeAddress: f.address.trim(), officeMapsUrl: f.mapsUrl.trim() }, c));
    }, 'setup.saved');
    if (cs) onSaved();
  };
  return (
    <form id="setup-form-office" className="stack" onSubmit={(e: { preventDefault(): void }) => void submit(e)} data-testid="setup-office">
      <div className="form-grid">
        <Field label={t('settings.office_address')} span2><input className="input" value={f.address} onChange={(e: { target: HTMLInputElement }) => setF({ ...f, address: e.target.value })} autoComplete="street-address" data-testid="setup-address" /></Field>
        <Field label={t('settings.office_maps')} hint={t('setup.https_hint')}><input className="input" type="url" value={f.mapsUrl} onChange={(e: { target: HTMLInputElement }) => setF({ ...f, mapsUrl: e.target.value })} /></Field>
        <Field label={t('setup.office_phone')}><input className="input" type="tel" value={f.phone} onChange={(e: { target: HTMLInputElement }) => setF({ ...f, phone: e.target.value })} /></Field>
        <div className="field span2" role="group" aria-label={t('setup.days')}><span>{t('setup.days')}</span>
          <div className="row wrap" style={{ gap: 6 }}>{[1, 2, 3, 4, 5, 6, 7].map((d) => <button key={d} type="button" className="chip toggle" aria-pressed={f.days.includes(d)} onClick={() => setF({ ...f, days: toggle(f.days, d).sort() })}>{dayName(d)}</button>)}</div>
        </div>
        <Field label={t('setup.from')}><input className="input" type="time" value={f.from} onChange={(e: { target: HTMLInputElement }) => setF({ ...f, from: e.target.value })} required /></Field>
        <Field label={t('setup.to')}><input className="input" type="time" value={f.to} onChange={(e: { target: HTMLInputElement }) => setF({ ...f, to: e.target.value })} required /></Field>
        <Field label={t('setup.meeting_minutes')}><select className="select" value={f.meetingMinutes} onChange={(e: { target: HTMLSelectElement }) => setF({ ...f, meetingMinutes: Number(e.target.value) })}>{[20, 30, 45, 60, 90].map((m) => <option key={m} value={m}>{t('setup.minutes', { n: m })}</option>)}</select></Field>
        <div className="field span2" role="group" aria-label={t('setup.modes')}><span>{t('setup.modes')}</span>
          <div className="row wrap" style={{ gap: 6 }}>{MEETING_MODES.map((m) => <button key={m} type="button" className="chip toggle" aria-pressed={f.modes.includes(m)} onClick={() => setF({ ...f, modes: toggle(f.modes, m) })}>{t(`setup.mode.${m}`)}</button>)}</div>
        </div>
      </div>
      <p className="tiny muted" style={{ margin: 0 }}>{t('setup.office_note')}</p>
    </form>
  );
}

function ClientsStep() {
  const rt = useRuntime();
  const { t } = rt;
  const n = rt.view.rows('persons').filter((p) => !p.archivedAt && (p.stage === 'client' || p.stage === 'prospect')).length;
  return (
    <div className="stack" data-testid="setup-clients">
      <p className={`callout small ${n ? 'ok' : ''}`} style={{ margin: 0 }}>{t('setup.clients_now', { count: n })}{rt.demo ? ` ${t('setup.clients_demo')}` : ''}</p>
      <div className="setup-choices">
        <Choice icon="upload" title={t('setup.clients_excel')} sub={t('setup.clients_excel_sub')} onClick={() => rt.open(<ClientImport />)} testId="setup-import-excel" primary />
        <Choice icon="data" title={t('setup.clients_vero')} sub={t('setup.clients_vero_sub')} onClick={() => navigate('/data')} />
        <Choice icon="plus" title={t('people.new')} sub={t('setup.clients_one_sub')} onClick={() => openers(rt).person()} />
        <Choice icon="camera" title={t('quickadd.card')} sub={t('setup.clients_card_sub')} onClick={() => navigate('/people')} />
      </div>
    </div>
  );
}
function Choice({ icon, title, sub, onClick, testId, primary }: { icon: string; title: string; sub: string; onClick: () => void; testId?: string; primary?: boolean }) {
  return <button type="button" className={`onboard-choice${primary ? ' primary' : ''}`} onClick={onClick} data-testid={testId}><span className="row" style={{ gap: 8 }}><Icon name={icon} size={16} /><b>{title}</b></span><span>{sub}</span></button>;
}

function ConnectStep() {
  const rt = useRuntime();
  const { t } = rt;
  const a = useAccount();
  const sig = useSetupSignals();
  return (
    <div className="stack" data-testid="setup-connect">
      {sig.outlookConnected ? <p className="callout ok small" style={{ margin: 0 }}>{t('setup.connect_done')}</p>
        : a?.signedIn ? <p className="callout small" style={{ margin: 0 }}>{t('setup.connect_ready')}</p>
          : <p className="callout warn small" style={{ margin: 0 }}>{t('setup.connect_sign_in')}</p>}
      <div className="setup-choices">
        <Choice icon="agenda" title={t('outlook.title')} sub={t('setup.connect_outlook_sub')} onClick={() => navigate('/account')} testId="setup-open-account" primary />
        <Choice icon="mail" title={t('setup.connect_mail')} sub={t('setup.connect_mail_sub')} onClick={() => navigate('/account')} />
        <Choice icon="download" title={t('setup.connect_ics')} sub={t('setup.connect_ics_sub')} onClick={() => navigate('/agenda')} />
      </div>
      <p className="tiny muted" style={{ margin: 0 }}>{t('setup.connect_note')}</p>
    </div>
  );
}

function BookingsStep({ onSaved }: { onSaved: () => void }) {
  const rt = useRuntime();
  const { t } = rt;
  const a = useAccount();
  const site = a?.website || 'https://avrastro.com';
  const [url, setUrl] = useState(bookingUrl(rt.view) ?? '');
  const submit = async (e: { preventDefault(): void }) => { e.preventDefault(); if (await rt.run((v, c) => saveBookingUrl(v, url, c), 'setup.saved')) onSaved(); };
  const copy = async () => { try { await navigator.clipboard.writeText(url); rt.toast(t('setup.copied'), 'info'); } catch { rt.toast(t('setup.copy_failed'), 'error'); } };
  return (
    <form id="setup-form-bookings" className="stack" onSubmit={(e: { preventDefault(): void }) => void submit(e)} data-testid="setup-bookings">
      <ol className="guide-steps">
        <li>{t('setup.bookings_1')}</li>
        <li>{t('setup.bookings_2')}</li>
        <li>{t('setup.bookings_3')}</li>
      </ol>
      <div className="row wrap" style={{ gap: 8 }}>
        <button type="button" className="btn" onClick={() => void openExternal(`${site.replace(/\/+$/, '')}/advisor-availability/`).catch(() => undefined)} data-testid="setup-open-availability"><Icon name="globe" />{t('setup.open_availability')}</button>
        <button type="button" className="btn btn-ghost" onClick={() => navigate('/inbox')}><Icon name="mail" />{t('nav.inbox')}</button>
      </div>
      <Field label={t('setup.booking_url')} hint={t('setup.https_hint')}>
        <div className="row" style={{ gap: 6 }}><input className="input grow" type="url" inputMode="url" value={url} onChange={(e: { target: HTMLInputElement }) => setUrl(e.target.value)} placeholder="https://avrastro.com/citizen-booking/?advisor=…" data-testid="setup-booking-url" />
          {url && <button type="button" className="btn btn-sm" onClick={() => void copy()}><Icon name="copy" size={14} />{t('setup.copy')}</button>}</div>
      </Field>
      <p className="tiny muted" style={{ margin: 0 }}>{t('setup.bookings_note')}</p>
    </form>
  );
}

function ModulesStep({ onSaved }: { onSaved: () => void }) {
  const rt = useRuntime();
  const { t } = rt;
  const pr = getPractice(rt.view);
  const [hidden, setHidden] = useState<Set<string>>(() => new Set(hiddenModules(rt.view)));
  const submit = async (e: { preventDefault(): void }) => { e.preventDefault(); if (await rt.run((v, c) => saveHiddenModules(v, [...hidden], c), 'setup.saved')) onSaved(); };
  const optional = MODULES.filter((m) => !ALWAYS_SHOWN_MODULES.includes(m.id) && !m.hidden);
  return (
    <form id="setup-form-modules" className="stack" onSubmit={(e: { preventDefault(): void }) => void submit(e)} data-testid="setup-modules">
      <p className="callout small" style={{ margin: 0 }}>{t(pr.lines.life && pr.lines.general ? 'setup.modules_both' : pr.lines.life ? 'setup.modules_life' : 'setup.modules_general')} <button type="button" className="linklike" onClick={() => navigate('/setup?step=profile')}>{t('setup.change_lines')}</button></p>
      <div className="setup-modules">
        {SPACES.map((sp) => { const mods = optional.filter((m) => m.space === sp.id); return mods.length ? (
          <fieldset key={sp.id} className="setup-space"><legend><Icon name={sp.icon} size={14} /> {t(sp.labelKey)}</legend>
            {mods.map((m) => <label key={m.id} className="check"><input type="checkbox" checked={!hidden.has(m.id)} onChange={(e: { target: HTMLInputElement }) => { const n = new Set(hidden); if (e.target.checked) n.delete(m.id); else n.add(m.id); setHidden(n); }} data-testid={`setup-module-${m.id}`} />{t(m.labelKey)}</label>)}
          </fieldset>) : null; })}
      </div>
      <p className="tiny muted" style={{ margin: 0 }}>{t('setup.modules_note')}</p>
    </form>
  );
}

function SafetyStep() {
  const rt = useRuntime();
  const { t } = rt;
  const sig = useSetupSignals();
  const age = sig.backupAgeDays;
  return (
    <div className="stack" data-testid="setup-safety">
      <ul className="protect-list setup-safety">
        <li><Icon name="download" size={15} /><span>{age == null ? t('setup.safety_no_backup') : t('setup.safety_backup_age', { days: age })}</span></li>
        <li><Icon name="lock" size={15} /><span>{sig.encrypted ? t('setup.safety_encrypted') : t('setup.safety_not_encrypted')} {!sig.encrypted && <button className="btn btn-sm btn-ghost" onClick={() => navigate('/security')}>{t('nav.security')}</button>}</span></li>
        <li><Icon name="shield" size={15} /><span>{t('setup.safety_account')} <button className="btn btn-sm btn-ghost" onClick={() => navigate('/account')}>{t('nav.account')}</button></span></li>
      </ul>
      <BackupCard />
    </div>
  );
}

function FinishStep({ p, go }: { p: SetupProgress; go: (s: SetupStep) => void }) {
  const rt = useRuntime();
  const { t } = rt;
  return (
    <div className="stack" data-testid="setup-finish">
      <div className="setup-panel-head">
        <span className="setup-panel-icon" aria-hidden="true"><Icon name="star" /></span>
        <div className="grow"><h2 id="setup-step-title">{t(p.complete ? 'setup.finish.done_title' : 'setup.finish.title')}</h2><p className="muted small" style={{ margin: 0 }}>{t(p.complete ? 'setup.finish.done_sub' : 'setup.finish.sub', { done: p.done, total: p.total })}</p></div>
      </div>
      <ol className="setup-checklist big">
        {p.items.map((it) => <li key={it.id} className={it.status}><button className="setup-check" onClick={() => go(it.id)}>
          <span className="setup-dot" aria-hidden="true">{it.status === 'done' ? <Icon name="check" size={13} /> : it.status === 'skipped' ? '–' : ''}</span>
          <span className="grow">{t(`setup.step.${it.id}.title`)}</span><span className={`chip ${it.status === 'done' ? 'ok' : it.status === 'skipped' ? '' : 'gold'}`}>{t(`setup.status.${it.status}`)}</span>
        </button></li>)}
      </ol>
      <div className="row wrap" style={{ gap: 8 }}>
        <button className="btn btn-primary" onClick={() => navigate('/now')} data-testid="setup-to-today">{t('setup.to_today')}</button>
        <button className="btn" onClick={() => navigate('/lessons')}><Icon name="presentation" />{t('nav.lessons')}</button>
        <button className="btn btn-ghost" onClick={() => { void rt.run((v, c) => saveSetting(v, 'tour.pending', true, c)).then(() => navigate('/now')); }}>{t('tour.show')}</button>
      </div>
    </div>
  );
}

/** a module or section is visible for this practice (licence classes + the advisor's choice) */
export function useShown(): (id: string) => boolean {
  const rt = useRuntime();
  return (id: string) => sectionShown(rt.view, id);
}

/** Settings: licence classes, registration and hidden tools at a glance (changed in the wizard) */
export function PracticeCard() {
  const rt = useRuntime();
  const { t } = rt;
  const pr = getPractice(rt.view);
  const hidden = hiddenModules(rt.view);
  const lines = [pr.lines.life && t('setup.line.life'), pr.lines.general && t('setup.line.general')].filter(Boolean).join(' · ');
  return (
    <section className="card card-pad" style={{ marginTop: 18 }} data-testid="practice-card">
      <div className="section-title">{t('setup.practice_title')}</div>
      <div className="kv">
        <div><span>{t('setup.lines')}</span><b data-testid="practice-lines">{lines}</b></div>
        <div><span>{t('setup.reg_no')}</span>{pr.regNo ?? '—'}</div>
        <div><span>{t('setup.capacity')}</span>{pr.capacity ? t(`setup.capacity_opt.${pr.capacity}`) : '—'}</div>
        <div><span>{t('setup.hidden_tools')}</span>{hidden.length ? hidden.map((id) => { const m = MODULES.find((x) => x.id === id); return m ? t(m.labelKey) : id; }).join(', ') : t('setup.none_hidden')}</div>
      </div>
      <div className="row wrap" style={{ gap: 8, marginTop: 12 }}>
        <button className="btn btn-sm" onClick={() => navigate('/setup?step=profile')} data-testid="practice-edit-lines">{t('setup.change_lines')}</button>
        <button className="btn btn-sm btn-ghost" onClick={() => navigate('/setup?step=modules')}>{t('setup.choose_tools')}</button>
      </div>
    </section>
  );
}
